import React, { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Printer, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Printable PTO / Holiday tracking sheet.
 *   • Loads every active, non-test employee.
 *   • Groups by department for easy read on paper.
 *   • Blank columns for the manager to hand-fill:
 *       - 2026 Weeks Available
 *       - Weeks / Days Taken
 *       - Notes
 *   • CMD+P / CTRL+P → clean B&W print.
 */
export default function HolidaySheetPage() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/timeclock/employees/all`);
        const active = (res.data || [])
          .filter((e) => e.active && !e.is_test)
          .sort((a, b) => {
            const d = (a.department || "").localeCompare(b.department || "");
            return d !== 0 ? d : a.name.localeCompare(b.name);
          });
        setEmployees(active);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Group by department
  const byDept = employees.reduce((acc, e) => {
    const d = e.department || "Other";
    (acc[d] = acc[d] || []).push(e);
    return acc;
  }, {});

  const printedAt = new Date().toLocaleDateString("en-US", {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  return (
    <>
      {/* ─── Print-only CSS ───────────────────────────────── */}
      <style>{`
        @media print {
          @page { size: letter; margin: 0.5in; }
          .no-print { display: none !important; }
          body { background: white !important; -webkit-print-color-adjust: exact; }
          .print-page { color: black !important; background: white !important; }
          .dept-block { page-break-inside: avoid; }
          .row-line { border-bottom: 1px solid #999 !important; }
          h1, h2, h3 { color: black !important; }
        }
        @media screen {
          .print-page { max-width: 8.5in; margin: 0 auto; }
        }
      `}</style>

      <div className="min-h-screen bg-gray-50 py-6 px-4">
        {/* Toolbar (screen only) */}
        <div className="no-print max-w-5xl mx-auto mb-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate("/paypunch")}
            data-testid="btn-back"
            className="text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <Button
            onClick={() => window.print()}
            className="bg-black hover:bg-gray-800 text-white"
            data-testid="btn-print"
          >
            <Printer className="w-4 h-4 mr-2" /> Print sheet
          </Button>
        </div>

        {/* Printable content */}
        <div className="print-page bg-white text-black shadow-sm border border-gray-200 p-8" data-testid="holiday-sheet">
          {/* Header */}
          <div className="flex items-start justify-between border-b-2 border-black pb-3 mb-4">
            <div>
              <h1 className="text-2xl font-bold tracking-tight">2026 Paid Time Off Tracker</h1>
              <p className="text-sm text-gray-600 mt-1">Market Place on Oakton · 4817 W Oakton St, Skokie IL</p>
            </div>
            <div className="text-right text-xs text-gray-600">
              <div>Printed: {printedAt}</div>
              <div>Employees: {employees.length}</div>
            </div>
          </div>

          <p className="text-[11px] text-gray-700 mb-4 leading-relaxed">
            Fill each row by hand. Send the finished sheet back to enter into the system.
            <span className="ml-2 font-mono bg-yellow-100 px-1">Weeks Available</span> = paid weeks granted for 2026.
            <span className="ml-2 font-mono bg-yellow-100 px-1">Taken</span> = days or weeks already used YTD.
          </p>

          {loading && <div className="text-center text-gray-500 py-8">Loading employees…</div>}

          {!loading &&
            Object.entries(byDept).map(([dept, emps]) => (
              <div key={dept} className="dept-block mb-6">
                <h3 className="text-sm font-bold uppercase tracking-wider bg-black text-white px-2 py-1 mb-0">
                  {dept} <span className="opacity-70 font-normal ml-1">({emps.length})</span>
                </h3>
                <table className="w-full border-collapse text-[13px]">
                  <thead>
                    <tr className="border-b-2 border-black">
                      <th className="text-left py-1 px-1 w-8 font-semibold">#</th>
                      <th className="text-left py-1 px-1 w-16 font-semibold">Code</th>
                      <th className="text-left py-1 px-2 font-semibold">Employee Name</th>
                      <th className="text-center py-1 px-1 w-28 font-semibold">Weeks Available (2026)</th>
                      <th className="text-center py-1 px-1 w-28 font-semibold">Taken (wks / days)</th>
                      <th className="text-left py-1 px-1 w-48 font-semibold">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {emps.map((e, i) => (
                      <tr key={e.code} className="row-line border-b border-gray-400 h-9">
                        <td className="py-1 px-1 align-middle text-gray-500">{i + 1}</td>
                        <td className="py-1 px-1 align-middle font-mono text-[12px]">{e.code}</td>
                        <td className="py-1 px-2 align-middle font-medium">{e.name}</td>
                        <td className="py-1 px-1 align-middle"></td>
                        <td className="py-1 px-1 align-middle"></td>
                        <td className="py-1 px-1 align-middle"></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}

          {/* Footer */}
          <div className="border-t border-black pt-3 mt-6 flex items-end justify-between text-[11px] text-gray-700">
            <div>
              <div>Prepared by: ____________________________</div>
              <div className="mt-3">Signature: ____________________________</div>
            </div>
            <div>
              <div>Approved by: ____________________________</div>
              <div className="mt-3">Date: ______________</div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
